/**
 * 🔥 PONT DE COMPATIBILITÉ FIREBASEAPI
 * 
 * Ce fichier maintient la compatibilité avec l'ancien système
 * tout en utilisant notre nouveau service simple en arrière-plan
 */

// Import direct depuis Firebase (plus de pont de compatibilité cassé)

import { database } from '../firebase';
import { ref, push, update, query, orderByChild, equalTo, startAt, onValue, runTransaction, get, remove } from 'firebase/database';
import { normalizeMeetingTimestamp, toIsoDate } from '../utils/dateHelpers';
import { geocodeAddress } from '../utils/geo';

/**
 * Normaliser un nom pour l'utiliser comme clé Firebase
 * Remplace les caractères interdits (. # $ / [ ]) par des underscores
 */
function normalizeFirebaseKey(name) {
  if (!name) return '';
  return String(name)
    .replace(/[.#$/\[\]]/g, '_') // Remplacer les caractères interdits par _
    .replace(/\s+/g, '_') // Remplacer les espaces par _
    .replace(/_+/g, '_') // Remplacer les underscores multiples par un seul
    .replace(/^_|_$/g, ''); // Enlever les underscores en début/fin
}

const normalizeRequest = (request) => {
  const normalizedTs = normalizeMeetingTimestamp(request);
  if (typeof normalizedTs === 'number' && normalizedTs !== request.meetingAt) {
    return {
      ...request,
      meetingAt: normalizedTs,
      isoDate: request.isoDate || toIsoDate(normalizedTs),
    };
  }
  return request;
};

// =============================================
// 🔄 FONCTIONS DE COMPATIBILITÉ - ANCIEN FORMAT
// =============================================

/**
 * Signature pour les watchers avec objet { meetingAtFilter, onData }
 */
export function watchOpenRequests({ meetingAtFilter, onData }) {
  console.log('🔄 [firebaseApi] watchOpenRequests - utilisation du pont de compatibilité', { meetingAtFilter });
  
  if (typeof onData !== 'function') {
    console.error('❌ [firebaseApi] onData invalide');
    return () => {};
  }

  try {
    const requestsRef = ref(database, 'requests');
    let q;

    // Appliquer le filtre par date si spécifié
    if (typeof meetingAtFilter === 'number') {
      q = query(requestsRef, orderByChild('meetingAt'), equalTo(meetingAtFilter));
    } else {
      // Ne prendre que les réunions futures (tolérance 30 minutes pour éviter les coupures brutales)
      const expirationTolerance = 30 * 60 * 1000;
      const startFrom = Date.now() - expirationTolerance;
      q = query(requestsRef, orderByChild('meetingAt'), startAt(startFrom));
    }
    
    const unsubscribe = onValue(q, (snapshot) => {
      try {
        if (!snapshot.exists()) {
          console.log('📭 [firebaseApi] Aucune donnée dans Firebase requests');
          onData([]);
          return;
        }

        const rawData = snapshot.val();
        const allRequests = Object.entries(rawData)
          .map(([id, data]) => ({
            id,
            ...data
          }))
          .map(normalizeRequest);

        // Filtrer les demandes non assignées ou avec places restantes
        let openRequests = allRequests.filter(request => {
          // Inclure si pas assignée
          if (!request.assigned) return true;
          
          // Sinon, vérifier s'il reste des places
          const seatsAssigned = Number(request.seatsAssigned || 0);
          const seatsNeeded = Number(request.seatsNeeded || 1);
          const seatsRemaining = Number(
            typeof request.seatsRemaining === 'number'
              ? request.seatsRemaining
              : Math.max(0, seatsNeeded - seatsAssigned)
          );
          
          return seatsRemaining > 0;
        });
        
        // Appliquer le filtre par date ISO si spécifié
        if (typeof meetingAtFilter === 'string') {
          openRequests = openRequests.filter(request => {
            const requestIsoDate = request.isoDate || new Date(request.meetingAt).toISOString().split('T')[0];
            return requestIsoDate === meetingAtFilter;
          });
          console.log(`📅 [firebaseApi] Filtrage par date ISO ${meetingAtFilter}: ${openRequests.length} demandes`);
        }
        
        // Filtrer les demandes expirées (tolérance 30 minutes après la réunion)
        const now = Date.now();
        const expirationTolerance = 30 * 60 * 1000; // 30 minutes
        const cutoff = now - expirationTolerance;
        const beforeFilter = openRequests.length;
        openRequests = openRequests.filter((request) => {
          if (typeof request.meetingAt !== "number") return false;
          if (request.meetingAt < cutoff) {
            console.log(
              "🗑️ [firebaseApi] Demande en attente expirée exclue:",
              request.id,
              new Date(request.meetingAt).toISOString(),
            );
            return false;
          }
          return true;
        });
        console.log(
          `⏰ [firebaseApi] Expiration en attente: ${beforeFilter - openRequests.length} demandes retirées (cutoff ${new Date(cutoff).toISOString()})`,
        );

        console.log(`🔄 [firebaseApi] ${openRequests.length} demandes ouvertes filtrées sur ${allRequests.length} total`);
        onData(openRequests);

      } catch (error) {
        console.error('❌ [firebaseApi] Erreur traitement snapshot watchOpenRequests:', error);
        onData([]);
      }
    }, (error) => {
      console.error('❌ [firebaseApi] Erreur Firebase listener watchOpenRequests:', error);
      onData([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur setup watchOpenRequests:', error);
    return () => {};
  }
}

export function watchAcceptedRequests({ meetingAtFilter, onData }) {
  console.log('🔄 [firebaseApi] watchAcceptedRequests - utilisation du pont de compatibilité', { meetingAtFilter });
  
  if (typeof onData !== 'function') {
    console.error('❌ [firebaseApi] onData invalide');
    return () => {};
  }

  try {
    const requestsRef = ref(database, 'requests');
    let q;

    // Appliquer le filtre par date si spécifié
    if (typeof meetingAtFilter === 'number') {
      q = query(requestsRef, orderByChild('meetingAt'), equalTo(meetingAtFilter));
    } else {
      const expirationTolerance = 30 * 60 * 1000;
      const startFrom = Date.now() - expirationTolerance;
      q = query(requestsRef, orderByChild('meetingAt'), startAt(startFrom));
    }
    
    const unsubscribe = onValue(q, (snapshot) => {
      try {
        if (!snapshot.exists()) {
          console.log('📭 [firebaseApi] Aucune donnée dans Firebase requests');
          onData([]);
          return;
        }

        const rawData = snapshot.val();
        const allRequests = Object.entries(rawData)
          .map(([id, data]) => ({
            id,
            ...data
          }))
          .map(normalizeRequest);

        console.log(`🔍 [firebaseApi] Total demandes récupérées: ${allRequests.length}`);
        console.log(`🔍 [firebaseApi] Demandes avec assigned=true: ${allRequests.filter(r => r.assigned === true).length}`);
        console.log(`🔍 [firebaseApi] Exemples de demandes assignées:`, 
          allRequests.filter(r => r.assigned === true).slice(0, 3).map(r => ({
            id: r.id,
            assigned: r.assigned,
            seatsAssigned: r.seatsAssigned,
            seatsNeeded: r.seatsNeeded,
            seatsRemaining: r.seatsRemaining,
            meetingAt: r.meetingAt ? new Date(r.meetingAt).toISOString() : null
          }))
        );

        // Filtrer les demandes assignées
        // Inclure les demandes qui ont au moins un conducteur (même partiellement remplies)
        let acceptedRequests = allRequests.filter(request => {
          const isAssigned = request.assigned === true;
          // Aussi inclure les demandes où toutes les places sont prises même si assigned n'est pas explicitement true
          const seatsAssigned = Number(request.seatsAssigned || 0);
          const seatsNeeded = Number(request.seatsNeeded || 1);
          const seatsRemaining = Number(request.seatsRemaining || 0);
          const allSeatsTaken = seatsRemaining === 0 && seatsAssigned >= seatsNeeded;
          
          // Inclure aussi les demandes qui ont au moins un conducteur (même partiellement remplies)
          const hasDrivers = request.drivers && Object.keys(request.drivers).length > 0;
          const hasSeatsAssigned = seatsAssigned > 0;
          
          const shouldInclude = isAssigned || allSeatsTaken || (hasDrivers && hasSeatsAssigned);
          
          // Logs détaillés pour déboguer
          console.log(`🔍 [firebaseApi] Évaluation demande ${request.id}:`, {
            name: request.name || request.passengerName,
            isAssigned: isAssigned,
            allSeatsTaken: allSeatsTaken,
            hasDrivers: hasDrivers,
            drivers: request.drivers ? Object.keys(request.drivers) : [],
            hasSeatsAssigned: hasSeatsAssigned,
            seatsAssigned: seatsAssigned,
            seatsNeeded: seatsNeeded,
            seatsRemaining: seatsRemaining,
            shouldInclude: shouldInclude
          });
          
          if (shouldInclude && hasDrivers) {
            const driverNames = Object.keys(request.drivers);
            const driverDetails = driverNames.map(name => {
              const driverData = request.drivers[name];
              return `${name}(${driverData?.seatsTaken || 1})`;
            }).join(', ');
            console.log(`✅ [firebaseApi] Demande acceptée incluse: ${request.id} (${request.name || request.passengerName}), drivers: [${driverDetails}], total: ${seatsAssigned}/${seatsNeeded}`);
          }
          
          return shouldInclude;
        });
        
        // Appliquer le filtre par date ISO si spécifié
        if (typeof meetingAtFilter === 'string') {
          acceptedRequests = acceptedRequests.filter(request => {
            const requestIsoDate = request.isoDate || new Date(request.meetingAt).toISOString().split('T')[0];
            return requestIsoDate === meetingAtFilter;
          });
          console.log(`📅 [firebaseApi] Filtrage par date ISO ${meetingAtFilter}: ${acceptedRequests.length} demandes acceptées`);
        }
        
        // Filtrer les demandes expirées (tolérance 30 minutes après la réunion)
        const now = Date.now();
        const expirationTolerance = 30 * 60 * 1000; // 30 minutes
        const cutoff = now - expirationTolerance;
        const beforeFilter = acceptedRequests.length;
        acceptedRequests = acceptedRequests.filter((request) => {
          if (typeof request.meetingAt !== "number") return false;
          if (request.meetingAt < cutoff) {
            console.log(
              "🗑️ [firebaseApi] Demande acceptée expirée exclue:",
              request.id,
              new Date(request.meetingAt).toISOString(),
            );
            return false;
          }
          return true;
        });
        console.log(
          `⏰ [firebaseApi] Expiration acceptées: ${beforeFilter - acceptedRequests.length} demandes retirées (cutoff ${new Date(cutoff).toISOString()})`,
        );

        console.log(`🔄 [firebaseApi] ${acceptedRequests.length} demandes acceptées filtrées sur ${allRequests.length} total`);
        onData(acceptedRequests);

      } catch (error) {
        console.error('❌ [firebaseApi] Erreur traitement snapshot watchAcceptedRequests:', error);
        onData([]);
      }
    }, (error) => {
      console.error('❌ [firebaseApi] Erreur Firebase listener watchAcceptedRequests:', error);
      onData([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur setup watchAcceptedRequests:', error);
    return () => {};
  }
}

// Alias pour la compatibilité
export const watchAllRequestsForCounts = watchOpenRequests;
export const watchAllAcceptedForCounts = watchAcceptedRequests;
export const watchAssignedRequests = watchAcceptedRequests;

/**
 * Création d'une nouvelle demande
 */
export async function createRequest({
  name,
  address,
  city,
  postalCode,
  meetingAt,
  isoDate,
  seatsNeeded = 1,
  icon,
  realAddress,
  latitude,
  longitude,
  description,
  returnDifferent,
  returnAddress,
  returnCity,
  returnPostalCode,
  returnRealAddress,
  returnLatitude,
  returnLongitude,
}) {
  console.log('🚶 [firebaseApi] createRequest - création de la demande:', {
    name,
    address,
    city,
    postalCode,
    meetingAt,
    isoDate,
    seatsNeeded,
    icon,
    realAddress,
    latitude,
    longitude,
    description,
    returnDifferent,
    returnAddress,
    returnCity,
    returnPostalCode,
    returnRealAddress,
    returnLatitude,
    returnLongitude,
  });
  
  try {
    if (!name || !address || !city || !postalCode || !meetingAt || !isoDate) {
      console.error('❌ [firebaseApi] Paramètres manquants pour createRequest');
      return { ok: false, error: 'Tous les paramètres sont requis' };
    }
    
    // Géocodage : utiliser les coordonnées GPS si disponibles, sinon l'adresse réelle, sinon l'adresse normale
    // Le géocodage est fait de manière asynchrone et non bloquante
    let geo = null;
    
    // Si on a des coordonnées GPS, les utiliser directement (pas besoin de géocodage)
    if (latitude && longitude) {
      geo = {
        lat: parseFloat(latitude),
        lon: parseFloat(longitude)
      };
      console.log('✅ [firebaseApi] Utilisation coordonnées GPS:', geo);
    }
    // Pour les autres cas, on ne fait PAS le géocodage ici pour ne pas bloquer
    // Le géocodage sera fait plus tard si nécessaire (dans RequestNearbyDriversModal par exemple)
    
    // Construire l'objet de données en excluant les valeurs undefined (Firebase ne les accepte pas)
    const requestData = {
      name,
      address, // Nom de l'adresse (ex: "Parking Monsabré") ou adresse normale
      city,
      postalCode,
      meetingAt,
      isoDate,
      seatsNeeded: Number(seatsNeeded) || 1,
      seatsAssigned: 0, // nombre de places déjà assignées
      seatsRemaining: Number(seatsNeeded) || 1, // nombre de places restantes
      createdAt: Date.now(),
      assigned: false,
      icon: icon || '👤', // icône du profil
    };
    
    // Ajouter les champs optionnels seulement s'ils ont une valeur
    if (description) {
      requestData.description = description;
    }
    if (realAddress) {
      requestData.realAddress = realAddress;
    }
    if (latitude) {
      requestData.latitude = latitude;
    }
    if (longitude) {
      requestData.longitude = longitude;
    }
    if (geo) {
      requestData.geo = geo;
    }

    // Champs "retour" (optionnels) — ne pas impacter le calcul de proximité (qui reste basé sur l'aller)
    // On les stocke uniquement si fournis (pas de undefined)
    if (returnDifferent !== undefined) {
      requestData.returnDifferent = Boolean(returnDifferent);
    }
    if (returnAddress) {
      requestData.returnAddress = returnAddress;
    }
    if (returnCity) {
      requestData.returnCity = returnCity;
    }
    if (returnPostalCode) {
      requestData.returnPostalCode = returnPostalCode;
    }
    if (returnRealAddress) {
      requestData.returnRealAddress = returnRealAddress;
    }
    if (returnLatitude) {
      requestData.returnLatitude = returnLatitude;
    }
    if (returnLongitude) {
      requestData.returnLongitude = returnLongitude;
    }
    
    const requestsRef = ref(database, 'requests');
    const newRequestRef = await push(requestsRef, requestData);
    
    console.log('✅ [firebaseApi] Demande créée avec succès:', newRequestRef.key, 'pour', seatsNeeded, 'places');
    return { ok: true, id: newRequestRef.key, key: newRequestRef.key };
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur createRequest:', error);
    return { ok: false, error: error.message };
  }
}

/**
 * Suppression d'une demande
 */
export async function deleteRequest(requestId) {
  console.log('🗑️ [firebaseApi] deleteRequest - suppression de la demande:', requestId);
  
  try {
    if (!requestId) {
      console.error('❌ [firebaseApi] requestId manquant');
      return { ok: false, error: 'ID de la demande manquant' };
    }
    
    const requestRef = ref(database, `requests/${requestId}`);
    
    // Vérifier que la demande existe avant de la supprimer
    const snapshot = await get(requestRef);
    if (!snapshot.exists()) {
      console.log('ℹ️ [firebaseApi] Demande déjà supprimée ou inexistante');
      return { ok: true };
    }
    
    // Supprimer la demande
    await remove(requestRef);
    
    console.log('✅ [firebaseApi] Demande supprimée avec succès:', requestId);
    return { ok: true };
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur deleteRequest:', error);
    return { ok: false, error: error.message };
  }
}

/**
 * Mise à jour d'une demande (adresse aller/retour)
 */
export async function updateRequest(requestId, updates = {}) {
  console.log("✏️ [firebaseApi] updateRequest:", { requestId, updates });

  try {
    if (!requestId) {
      return { ok: false, error: "ID de la demande manquant" };
    }

    const requestRef = ref(database, `requests/${requestId}`);
    const snapshot = await get(requestRef);
    if (!snapshot.exists()) {
      return { ok: false, error: "Demande introuvable" };
    }

    const payload = {
      updatedAt: Date.now(),
    };

    const setIfDefined = (key) => {
      if (Object.prototype.hasOwnProperty.call(updates, key)) {
        payload[key] = updates[key];
      }
    };

    setIfDefined("address");
    setIfDefined("city");
    setIfDefined("postalCode");
    setIfDefined("realAddress");
    setIfDefined("latitude");
    setIfDefined("longitude");
    setIfDefined("returnDifferent");
    setIfDefined("returnAddress");
    setIfDefined("returnCity");
    setIfDefined("returnPostalCode");
    setIfDefined("returnRealAddress");
    setIfDefined("returnLatitude");
    setIfDefined("returnLongitude");

    await update(requestRef, payload);
    return { ok: true };
  } catch (error) {
    console.error("❌ [firebaseApi] Erreur updateRequest:", error);
    return { ok: false, error: error.message };
  }
}

// =============================================
// 🚗 FONCTIONS CONDUCTEUR - IMPLÉMENTATION SIMPLIFIÉE
// =============================================

export async function createDriverOffer(offerData) {
  console.log('🚗 [firebaseApi] createDriverOffer', offerData);
  
  try {
    // Construire l'objet en excluant les valeurs undefined (Firebase ne les accepte pas)
    const driverOfferData = {
      driverName: offerData.driverName,
      startAddress: offerData.startAddress,
      city: offerData.city,
      postalCode: offerData.postalCode,
      seats: Number(offerData.seats) || 1,
      meetingAt: offerData.meetingAt,
      isoDate: offerData.isoDate,
      createdAt: Date.now(),
      icon: offerData.icon || '🚗', // icône du conducteur
    };
    
    // Ajouter les champs optionnels seulement s'ils ont une valeur
    if (offerData.seatsTaken !== undefined) {
      driverOfferData.seatsTaken = Number(offerData.seatsTaken) || 0;
    }
    if (offerData.seatsAvailable !== undefined) {
      driverOfferData.seatsAvailable = Number(offerData.seatsAvailable) || driverOfferData.seats;
    }

    const newRef = await push(ref(database, 'driverOffers'), driverOfferData);
    console.log('✅ [firebaseApi] Offre conducteur créée avec succès:', newRef.key);
    return { ok: true, id: newRef.key, key: newRef.key };
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur createDriverOffer:', error);
    return { ok: false, error: error.message };
  }
}

export function watchDriverOffers(onData, onError) {
  console.log('🚗 [firebaseApi] watchDriverOffers');
  
  if (typeof onData !== 'function') {
    console.error('❌ [firebaseApi] onData invalide pour watchDriverOffers');
    return () => {};
  }

  try {
    const offersRef = ref(database, 'driverOffers');
    
    const unsubscribe = onValue(offersRef, (snapshot) => {
      try {
        if (!snapshot.exists()) {
          onData([]);
          return;
        }

        const rawData = snapshot.val();
        const offers = Object.entries(rawData).map(([id, data]) => ({
          id,
          ...data
        }));

        console.log(`🚗 [firebaseApi] ${offers.length} offres conducteur trouvées`);
        onData(offers);
      } catch (error) {
        console.error('❌ [firebaseApi] Erreur traitement watchDriverOffers:', error);
        onData([]);
      }
    }, (error) => {
      console.error('❌ [firebaseApi] Erreur Firebase watchDriverOffers:', error);
      if (typeof onError === 'function') onError(error);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur setup watchDriverOffers:', error);
    return () => {};
  }
}

export function watchDriverOffersByMeeting({ meetingAt, isoDate, onData, onError }) {
  console.log('🚗 [firebaseApi] watchDriverOffersByMeeting', { meetingAt, isoDate });
  
  if (typeof onData !== 'function') {
    console.error('❌ [firebaseApi] onData invalide pour watchDriverOffersByMeeting');
    return () => {};
  }

  try {
    const offersRef = ref(database, 'driverOffers');
    let q;

    // Priorité à isoDate pour éviter les problèmes de fuseau horaire
    if (typeof isoDate === 'string' && isoDate) {
      q = query(offersRef, orderByChild('isoDate'), equalTo(isoDate));
      console.log('🔍 [firebaseApi] Recherche par isoDate:', isoDate);
    } else if (typeof meetingAt === 'number') {
      q = query(offersRef, orderByChild('meetingAt'), equalTo(meetingAt));
      console.log('🔍 [firebaseApi] Recherche par meetingAt:', meetingAt);
    } else {
      q = offersRef;
      console.log('🔍 [firebaseApi] Recherche sans filtre');
    }
    
    const unsubscribe = onValue(q, (snapshot) => {
      try {
        if (!snapshot.exists()) {
          console.log('📭 [firebaseApi] Aucune offre trouvée');
          onData([]);
          return;
        }

        const rawData = snapshot.val();
        let offers = Object.entries(rawData).map(([id, data]) => ({
          id,
          ...data
        }));

        // Si on a recherché par isoDate, on peut aussi filtrer par meetingAt normalisé pour plus de précision
        if (typeof isoDate === 'string' && typeof meetingAt === 'number') {
          const normalizedMeetingAt = normalizeMeetingTimestamp({ meetingAt, isoDate });
          offers = offers.filter(offer => {
            const offerNormalized = normalizeMeetingTimestamp(offer);
            // Accepter si le meetingAt normalisé correspond (tolérance de 1 minute pour les arrondis)
            return Math.abs((offerNormalized || offer.meetingAt) - normalizedMeetingAt) < 60000;
          });
          console.log(`🔍 [firebaseApi] Après filtrage par meetingAt normalisé: ${offers.length} offres`);
        }

        console.log(`🚗 [firebaseApi] ${offers.length} offres pour meeting filtrées`);
        onData(offers);
      } catch (error) {
        console.error('❌ [firebaseApi] Erreur traitement watchDriverOffersByMeeting:', error);
        onData([]);
      }
    }, (error) => {
      console.error('❌ [firebaseApi] Erreur Firebase watchDriverOffersByMeeting:', error);
      if (typeof onError === 'function') onError(error);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur setup watchDriverOffersByMeeting:', error);
    return () => {};
  }
}

// =============================================
// 🎯 FONCTIONS D'ASSIGNATION DE DEMANDES
// =============================================

export async function takeSeatForRequest({ requestId, driverOfferId, driverName, seatsToTake = 1 }) {
  console.log(`🎯 [firebaseApi] takeSeatForRequest: ${requestId} par ${driverName} pour ${seatsToTake} place(s)`);
  
  try {
    const requestRef = ref(database, `requests/${requestId}`);

    // Récupérer l'icône du conducteur depuis son offre
    let driverIcon = '🚗'; // icône par défaut
    if (driverOfferId) {
      try {
        const offerRef = ref(database, `driverOffers/${driverOfferId}`);
        const offerSnapshot = await get(offerRef);
        if (offerSnapshot.exists()) {
          const offerData = offerSnapshot.val();
          driverIcon = offerData.icon || '🚗';
        }
      } catch (error) {
        console.warn('⚠️ [firebaseApi] Impossible de récupérer l\'icône du conducteur:', error);
      }
    }

    // Transaction pour éviter les conflits et gérer les places partielles
    const result = await runTransaction(requestRef, (currentData) => {
      if (!currentData) {
        return; // Annuler si pas de données
      }

      const currentSeatsRemaining = currentData.seatsRemaining || currentData.seatsNeeded || 1;
      
      if (currentSeatsRemaining < seatsToTake) {
        console.log(`❌ [firebaseApi] Pas assez de places disponibles: ${currentSeatsRemaining} < ${seatsToTake}`);
        return; // Annuler si pas assez de places
      }

      const newSeatsRemaining = currentSeatsRemaining - seatsToTake;
      const newSeatsAssigned = (currentData.seatsAssigned || 0) + seatsToTake;
      
      // Normaliser le nom du conducteur pour créer une clé Firebase valide
      const normalizedDriverKey = normalizeFirebaseKey(driverName);
      const prevTakenByDriver = (currentData.drivers && currentData.drivers[normalizedDriverKey]?.seatsTaken) || 0;
      
      // Si toutes les places sont prises, marquer comme complètement assigné
      const fullyAssigned = newSeatsRemaining === 0;
      
      console.log(`🔍 [firebaseApi] takeSeatForRequest - État des places:`, {
        requestId,
        driverName,
        normalizedDriverKey,
        currentSeatsRemaining,
        seatsToTake,
        newSeatsRemaining,
        newSeatsAssigned,
        seatsNeeded: currentData.seatsNeeded || 1,
        fullyAssigned,
        previousAssigned: currentData.assigned
      });
      
      // Construire l'objet drivers avec la clé normalisée
      const newDrivers = { ...(currentData.drivers || {}) };
      newDrivers[normalizedDriverKey] = {
        driverName: driverName, // Conserver le nom original pour l'affichage
        seatsTaken: prevTakenByDriver + seatsToTake,
        assignedAt: Date.now(),
        driverOfferId,
        icon: driverIcon // Ajouter l'icône du conducteur
      };
      
      return {
        ...currentData,
        assigned: fullyAssigned, // true seulement si toutes les places sont prises
        seatsRemaining: newSeatsRemaining,
        seatsAssigned: newSeatsAssigned,
        assignedTo: driverName,
        assignedOfferId: driverOfferId,
        assignedAt: Date.now(),
        // Garder une trace des conducteurs qui ont pris des places (avec clé normalisée)
        drivers: newDrivers
      };
    });

    if (result.committed) {
      // Mettre à jour les places disponibles du conducteur
      if (driverOfferId) {
        const offerRef = ref(database, `driverOffers/${driverOfferId}`);
        const offerSnapshot = await get(offerRef);

        if (offerSnapshot.exists()) {
          const offerData = offerSnapshot.val();
          // Calculer les places disponibles : places totales - places déjà prises
          const totalSeats = offerData.seats || 1;
          const seatsTaken = offerData.seatsTaken || 0;
          const newSeatsTaken = seatsTaken + seatsToTake;
          const newSeatsAvailable = Math.max(0, totalSeats - newSeatsTaken);
          
          console.log(`🚗 [firebaseApi] Mise à jour places conducteur: ${totalSeats} total, ${newSeatsTaken} prises, ${newSeatsAvailable} disponibles`);
          
          await update(offerRef, {
            seatsAvailable: newSeatsAvailable,
            seatsTaken: newSeatsTaken
          });
        }
      }
      
      console.log(`✅ [firebaseApi] ${seatsToTake} place(s) assignée(s) à ${driverName}`);
      return { ok: true, seatsTaken: seatsToTake };
    }

    return { ok: false, error: 'Conflit de concurrence ou places insuffisantes' };
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur takeSeatForRequest:', error);
    return { ok: false, error: error.message };
  }
}

export async function cancelDriverHelp({ requestId, driverOfferId, driverName }) {
  console.log(`🎯 [firebaseApi] cancelDriverHelp: ${requestId} par ${driverName}`);
  
  try {
    const requestRef = ref(database, `requests/${requestId}`);

    // Normaliser le nom du conducteur pour créer une clé Firebase valide
    const normalizedDriverKey = normalizeFirebaseKey(driverName);

    // Lire l'état avant transaction pour récupérer l'offerId et les places prises par le conducteur
    let offerIdToDelete = driverOfferId;
    let seatsTakenByDriverBefore = 1; // Valeur par défaut
    try {
      const prevSnap = await get(requestRef);
      const prevData = prevSnap.val();
      // Chercher avec la clé normalisée
      if (!offerIdToDelete && driverName && prevData?.drivers?.[normalizedDriverKey]) {
        offerIdToDelete = prevData.drivers[normalizedDriverKey].driverOfferId || null;
      }
      // Récupérer le nombre de places prises AVANT la transaction
      if (driverName && prevData?.drivers?.[normalizedDriverKey]) {
        seatsTakenByDriverBefore = prevData.drivers[normalizedDriverKey].seatsTaken || 1;
      }
    } catch {}

    const result = await runTransaction(requestRef, (currentData) => {
      console.log('🔍 [firebaseApi] Transaction data:', {
        assigned: currentData?.assigned,
        assignedTo: currentData?.assignedTo,
        driverName: currentData?.driverName,
        providedDriverName: driverName
      });
      
      if (!currentData) {
        console.log('❌ [firebaseApi] Pas de données dans la transaction');
        return;
      }

      // Si un driverName est fourni et présent dans la liste des drivers, annuler seulement ce conducteur
      // Utiliser la clé normalisée pour accéder aux données
      if (driverName && currentData.drivers && currentData.drivers[normalizedDriverKey]) {
        const seatsTakenByDriver = currentData.drivers[normalizedDriverKey].seatsTaken || 1;
        const newDrivers = { ...(currentData.drivers || {}) };
        delete newDrivers[normalizedDriverKey];

        const newSeatsAssigned = Math.max(0, (currentData.seatsAssigned || 0) - seatsTakenByDriver);
        const originalNeeded = currentData.seatsNeeded || 1;
        const newSeatsRemaining = Math.max(0, originalNeeded - newSeatsAssigned);
        const stillAssigned = newSeatsRemaining === 0; // assigné uniquement si toutes les places sont prises

        return {
          ...currentData,
          assigned: stillAssigned,
          assignedTo: stillAssigned ? currentData.assignedTo : null,
          assignedOfferId: stillAssigned ? currentData.assignedOfferId : null,
          seatsAssigned: newSeatsAssigned,
          seatsRemaining: newSeatsRemaining,
          drivers: newDrivers,
          lastCancelledBy: driverName,
          lastCancelledAt: Date.now()
        };
      }

      // Sinon: comportement précédent (annuler totalement l'aide)
      return {
        ...currentData,
        assigned: false,
        assignedTo: null,
        assignedOfferId: null,
        assignedAt: null,
        seatsAssigned: 0,
        seatsRemaining: currentData.seatsNeeded || 1,
        drivers: {}
      };
    });

    if (result.committed) {
      // Remettre à jour les places disponibles du conducteur au lieu de supprimer l'offre
      if (offerIdToDelete) {
        try {
          const offerRef = ref(database, `driverOffers/${offerIdToDelete}`);
          const offerSnapshot = await get(offerRef);

          if (offerSnapshot.exists()) {
            const offerData = offerSnapshot.val();
            
            // Utiliser le nombre de places prises récupéré AVANT la transaction
            // (car après la transaction, le conducteur n'est plus dans drivers)
            const seatsTakenByDriver = seatsTakenByDriverBefore;
            
            // Recalculer les places disponibles : places totales - places encore prises
            const totalSeats = offerData.seats || 1;
            const currentSeatsTaken = offerData.seatsTaken || 0;
            const newSeatsTaken = Math.max(0, currentSeatsTaken - seatsTakenByDriver);
            const newSeatsAvailable = Math.max(0, totalSeats - newSeatsTaken);
            
            console.log(`🚗 [firebaseApi] Remise à jour places conducteur après annulation:`, {
              driverName,
              totalSeats,
              currentSeatsTaken,
              seatsTakenByDriver,
              newSeatsTaken,
              newSeatsAvailable
            });
            
            await update(offerRef, {
              seatsAvailable: newSeatsAvailable,
              seatsTaken: newSeatsTaken
            });
          }
        } catch (e) {
          console.warn('⚠️ [firebaseApi] Mise à jour des places du conducteur échouée:', e);
        }
      }
      
      return { ok: true };
    }

    return { ok: false, error: 'Conflit de concurrence ou demande non assignée' };
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur cancelDriverHelp:', error);
    return { ok: false, error: error.message };
  }
}

export async function deleteDriverOffer({ driverOfferId, driverName, cancelAllDates = false }) {
  console.log(`🗑️ [firebaseApi] deleteDriverOffer:`, { driverOfferId, driverName, cancelAllDates });
  
  try {
    // 1. Supprimer l'offre ou toutes les offres du conducteur
    if (cancelAllDates && driverName) {
      console.log(`🗓️ [firebaseApi] Suppression de TOUTES les offres pour ${driverName}`);
      try {
        const offersRef = ref(database, 'driverOffers');
        const q = query(offersRef, orderByChild('driverName'), equalTo(driverName));
        const snap = await get(q);
        if (snap.exists()) {
          const offers = snap.val();
          const updates = {};
          Object.keys(offers).forEach((id) => {
            updates[`driverOffers/${id}`] = null;
          });
          if (Object.keys(updates).length > 0) {
            await update(ref(database), updates);
            console.log(`✅ [firebaseApi] ${Object.keys(updates).length} offre(s) supprimée(s) pour ${driverName}`);
          }
        } else {
          console.log(`ℹ️ [firebaseApi] Aucune offre trouvée pour ${driverName}`);
        }
      } catch (indexErr) {
        console.warn('⚠️ [firebaseApi] Index driverName absent pour driverOffers, fallback plein scan...');
        const offersRef = ref(database, 'driverOffers');
        const snapAll = await get(offersRef);
        if (snapAll.exists()) {
          const offers = snapAll.val();
          const updates = {};
          Object.entries(offers).forEach(([id, offer]) => {
            if (offer.driverName === driverName) {
              updates[`driverOffers/${id}`] = null;
            }
          });
          if (Object.keys(updates).length > 0) {
            await update(ref(database), updates);
            console.log(`✅ [firebaseApi] ${Object.keys(updates).length} offre(s) supprimée(s) pour ${driverName} (fallback)`);
          }
        }
      }
    } else {
      if (!driverOfferId) {
        console.error('❌ [firebaseApi] driverOfferId manquant');
        return { ok: false, error: 'ID de l\'offre manquant' };
      }
      const offerRef = ref(database, `driverOffers/${driverOfferId}`);
      await remove(offerRef);
      console.log(`✅ [firebaseApi] Offre conducteur ${driverOfferId} supprimée !`);
    }
    
    // 2. Remettre en attente toutes les demandes assignées à ce conducteur
    if (driverName) {
      console.log(`🔄 [firebaseApi] Recherche des demandes assignées à ${driverName}...`);
      
      try {
        const requestsRef = ref(database, 'requests');
        const q = query(requestsRef, orderByChild('driverName'), equalTo(driverName));
        
        const snapshot = await get(q);
        if (snapshot.exists()) {
          const requests = snapshot.val();
          const updates = {};
          
          Object.entries(requests).forEach(([requestId, request]) => {
            if (request.assigned && request.driverName === driverName) {
              console.log(`🔄 [firebaseApi] Remise en attente de la demande ${requestId}`);
              updates[`requests/${requestId}/assigned`] = false;
              updates[`requests/${requestId}/driverName`] = null;
              updates[`requests/${requestId}/assignedOfferId`] = null;
              updates[`requests/${requestId}/assignedAt`] = null;
              updates[`requests/${requestId}/cancelledAt`] = Date.now();
              updates[`requests/${requestId}/cancelledByDriver`] = driverName;
            }
          });
          
          if (Object.keys(updates).length > 0) {
            await update(ref(database), updates);
            console.log(`✅ [firebaseApi] ${Object.keys(updates).length / 6} demandes remises en attente`);
          } else {
            console.log(`ℹ️ [firebaseApi] Aucune demande assignée trouvée pour ${driverName}`);
          }
        } else {
          console.log(`ℹ️ [firebaseApi] Aucune demande trouvée pour ${driverName}`);
        }
      } catch (indexError) {
        console.warn(`⚠️ [firebaseApi] Index driverName non disponible, recherche alternative...`);
        
        // Recherche alternative : récupérer toutes les demandes et filtrer côté client
        const requestsRef = ref(database, 'requests');
        const snapshot = await get(requestsRef);
        
        if (snapshot.exists()) {
          const requests = snapshot.val();
          const updates = {};
          
          Object.entries(requests).forEach(([requestId, request]) => {
            if (request.assigned && request.driverName === driverName) {
              console.log(`🔄 [firebaseApi] Remise en attente de la demande ${requestId} (recherche alternative)`);
              updates[`requests/${requestId}/assigned`] = false;
              updates[`requests/${requestId}/driverName`] = null;
              updates[`requests/${requestId}/assignedOfferId`] = null;
              updates[`requests/${requestId}/assignedAt`] = null;
              updates[`requests/${requestId}/cancelledAt`] = Date.now();
              updates[`requests/${requestId}/cancelledByDriver`] = driverName;
            }
          });
          
          if (Object.keys(updates).length > 0) {
            await update(ref(database), updates);
            console.log(`✅ [firebaseApi] ${Object.keys(updates).length / 6} demandes remises en attente (recherche alternative)`);
          }
        }
      }
    }
    
    return { ok: true };
  } catch (error) {
    console.error('❌ [firebaseApi] Erreur deleteDriverOffer:', error);
    return { ok: false, error: error.message };
  }
}

// =============================================
// 🧹 FONCTIONS DE NETTOYAGE
// =============================================

console.log('✅ [firebaseApi] Pont de compatibilité chargé avec succès');

// Export par défaut pour la compatibilité
export default {
  watchOpenRequests,
  watchAcceptedRequests,
  watchAllRequestsForCounts,
  watchAllAcceptedForCounts,
  watchAssignedRequests,
  createRequest,
  updateRequest,
  deleteRequest,
  createDriverOffer,
  deleteDriverOffer,
  watchDriverOffers,
  watchDriverOffersByMeeting,
  takeSeatForRequest,
  cancelDriverHelp
};